/**
 * THE STAMINA-BOOST AURA — sparse red embers rising off the player spending the bar.
 *
 * Owner, 2026-08-13: *"add a red aura on player when he activates it please"*,
 * about the `Digit1` boost (constants.ts's BOOST ECONOMY, InputFlags.Boost1/2).
 * The first build followed a Dragon-Ball-power-up reference literally — a
 * swollen glowing shell around the whole silhouette. Owner, having seen it:
 * *"fix that aura thing... make it visually look good and show it subtle not
 * too heavy and make it like particle coming from the player"*. That is not a
 * tuning note on the shell, it is a different effect: a HANDFUL OF EMBERS
 * leaving specific points on his body, not a glow wrapping all of him.
 *
 * ⚠ THE SHELL (swollen inverted-hull, full-body fresnel flame) IS GONE, on
 * purpose, not tuned down. A shell reads as "the character is glowing"; a
 * sparse particle stream reads as "something is coming off him", which is what
 * "subtle" and "particle" both ask for at once — there is no dial on the old
 * approach that turns a filled silhouette into a scatter of points, because the
 * old approach never had individual points, only a continuous band.
 *
 * ── WHERE THE EMBERS COME FROM: NAMED BONES, NOT THE MESH ──────────────────
 *
 * The old hull cloned the player's own skinned geometry so it could not miss a
 * pose. A handful of sparse points do not need that: they need a small set of
 * WORLD POSITIONS that move with him, and this game's rig already publishes
 * exactly that shape of answer for hands (`handAttach.ts`'s `findHandRig`).
 * This file adds the same lookup for feet and reuses the hand one outright —
 * four limb points plus the hips, five emitters, each just a bone's
 * `matrixWorld` position read once a frame. No vertex work, no shader patch on
 * the character, no geometry clone of him.
 *
 * ⚠ SAME LOD-SWAP TRAP THE HAND SEAM DOCUMENTS, SAME FIX. `castPlayerVisual.ts`
 * gives every LOD tier its own skeleton and swaps tiers by camera distance; a
 * bone captured from tier N is meaningless once the slot is drawing tier M.
 * The emitter remembers which `SilhouetteProvider` array it resolved its bones
 * from (by identity) and re-resolves the moment that identity changes, so a
 * mid-boost LOD swap re-anchors instead of emitting from a bone that is no
 * longer on screen.
 *
 * ── COST ─────────────────────────────────────────────────────────────────
 *
 * One `InstancedMesh`, `MAX_PARTICLES` camera-facing quads, alpha blended, no
 * texture (a procedural soft-circle fragment). Nothing at all for players not
 * boosting — `update()` returns after a couple of comparisons when `level` is
 * 0. Particle state is five flat `Float32Array`s sized once at init; nothing
 * is allocated per frame except the transient JS objects the spawn loop pushes
 * (bounded by `MAX_PARTICLES`, and popped off the same array, so the pool
 * self-limits rather than growing).
 *
 * Presentation only (§15.2): reads RenderState, writes nothing back.
 */
import {
  Color,
  DynamicDrawUsage,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  NormalBlending,
  PlaneGeometry,
  ShaderMaterial,
  Vector3,
  type Object3D,
} from 'three';
import type { RenderContext, RenderState, RenderSubsystem } from './context.js';
import { findHandRig, type HandRig } from './handAttach.js';

/**
 * Foot bone names, mirroring `handAttach.ts`'s `LEFT_WRIST`/`RIGHT_WRIST`
 * exactly — lowercased, `armature:`/`mixamorig5:`-prefix-stripped, exact
 * match only. The 74-bone production rig is `foot_l`/`foot_r`
 * (`authoredSkeletonContract.ts`); the rest cover the Mixamo test rig so this
 * file survives a rig swap the way the hand lookup already does.
 */
const LEFT_FOOT = ['foot_l', 'foot.l', 'l_foot', 'leftfoot'];
const RIGHT_FOOT = ['foot_r', 'foot.r', 'r_foot', 'rightfoot'];
const HIPS = ['hips', 'pelvis', 'hip'];

/** Exported for `boostThunder.ts` — the tier-2 lightning wants the SAME five
 *  body anchors the embers use (so a bolt jumps between real limbs, not a
 *  second, disagreeing set of points), not a second bone-name lookup. */
export interface EmberRig {
  readonly points: readonly Object3D[];
}

function normalizeBoneName(name: string): string {
  const colon = name.lastIndexOf(':');
  return (colon >= 0 ? name.slice(colon + 1) : name).toLowerCase();
}

export function findEmberRig(bones: readonly Object3D[], hands: HandRig): EmberRig {
  const byName = new Map<string, Object3D>();
  for (const b of bones) {
    const n = normalizeBoneName(b.name);
    if (!byName.has(n)) byName.set(n, b);
  }
  const pick = (candidates: readonly string[]): Object3D | null => {
    for (const c of candidates) {
      const hit = byName.get(c);
      if (hit) return hit;
    }
    return null;
  };
  // Five candidate points: two feet, two hands (reused from the hold rig so
  // this effect and a held ball can never disagree about where a hand is),
  // hips as a torso anchor. Nulls are filtered out below — a rig missing one
  // name (a stripped-down debug skeleton, say) still emits from the rest.
  const all = [pick(LEFT_FOOT), pick(RIGHT_FOOT), hands.leftWrist, hands.rightWrist, pick(HIPS)];
  return { points: all.filter((p): p is Object3D => p !== null) };
}

/** Alive particles across every boosting slot combined (there is realistically
 *  ever one, but the cap is global so a pathological case cannot run away).
 *  Sized for several simultaneous boosters at the spawn rate below —
 *  steady-state for one tier-2 booster alone is ~151 (144 * 1.05s life). */
const MAX_PARTICLES = 640;
/** Embers spawned per second, tier-indexed. Index 0 unused (tier 0 = off).
 *  Owner, 2026-08-13: "increase the particles please for 1 and for boost 2
 *  as well make it 4x", then "increase more please", then "increase the
 *  particles more please" — was [0, 5, 9]. */
const SPAWN_RATE: readonly number[] = [0, 80, 144];
/** Seconds an ember lives before it has fully faded. */
const LIFE_S: readonly number[] = [0, 0.85, 1.05];
/** Metres/second, mostly-upward drift. Owner, 2026-08-13: "for boost 2 can
 *  you make the particles rise above the player" — tier 2's raised from 0.85
 *  to 1.8, which times its 1.05s life is ~1.89m of average rise (range
 *  ~1.4-2.4m with the spawn's 0.75-1.25x jitter factor), enough to clear the
 *  1.8039m master rig height even from a foot-level spawn point. Tier 1 is
 *  unchanged — this was asked for tier 2 only. */
const RISE_SPEED: readonly number[] = [0, 0.55, 1.8];
/** Metres, world-space radius of jitter around the emission bone at spawn —
 *  keeps embers from all leaving from exactly the same point every time. */
const SPAWN_JITTER_M = 0.05;
/** Metres/second, sideways wander speed (not rise) — small, so the read stays
 *  "rising", not "scattering". */
const DRIFT_SPEED_M_S = 0.10;
/** World size of one ember quad, metres, at three points in its life: fresh,
 *  mid-life peak, and just before it winks out. Small and round throughout —
 *  this is a spark, not a puff of smoke. */
const SIZE_START = 0.018;
const SIZE_PEAK = 0.040;
const SIZE_END = 0.006;

/** Same dark-red-survives-ACES lesson the retired shell learned, kept: each
 *  tier's core is a hot young-ember colour easing to an edge colour it fades
 *  out on, because the tone-mapper this game grades through (§6.1, postfx.ts)
 *  pushes bright saturated red toward orange at high luminance — a hot pure
 *  red photographs amber. Tier-indexed (index 0 unused, tier 0 = off).
 *  Tier 1 raised a brightness step on 2026-08-13 ("make them a little
 *  bright", from [0xffb070, 0xa60300]) and is unchanged here. Tier 2 is a
 *  separate, deliberately darker pair — Owner, 2026-08-13: "for boost 2 ...
 *  make them dark red" — so the two tiers read as visually distinct, not
 *  just "more of the same colour". */
const CORE_COLOR: readonly number[] = [0, 0xffd0a0, 0x8f0000];
const EDGE_COLOR: readonly number[] = [0, 0xc21f00, 0x300000];

/** Owner, 2026-08-13: "make it glow please". This game already has a real
 *  bloom pass (`postfx.ts`'s `BLOOM_LUMINANCE_THRESHOLD = 1.0`, HalfFloat HDR
 *  buffers, order SSAO -> Bloom -> ... -> ACES) — a glow here is not a shader
 *  trick drawn on the quad itself, it is authoring a colour bright enough to
 *  cross that threshold and let the EXISTING pass do the halo, the same way
 *  every other bright highlight in this scene blooms. Applied to `vColor`
 *  before it hits `gl_FragColor`, scaled by alpha as always, so the glow is
 *  strongest where an ember is most opaque (its hot centre, early in life)
 *  and fades with it — not a flat brightness added everywhere regardless of
 *  shape. At tier 1's warm core this clears the threshold outright; at tier
 *  2's dark red it only just crosses it in the brightest young-ember instant
 *  and falls back under it as the colour eases toward the dark edge tone —
 *  a hot core cooling into a dark coal, not a contradiction of "dark red". */
const GLOW_INTENSITY = 2.2;

/** Owner, 2026-08-13: "can you add trails to those particles?" A trail here is
 *  not history geometry (no per-particle position buffer, no extra draw
 *  calls) — it is the SAME billboard quad stretched along the ember's current
 *  velocity, compact on the leading edge and long on the trailing one, like a
 *  spark. `aVelocity` carries each instance's current world-space velocity
 *  (`ivx+vx, ivy+vy, ivz+vz` — the same sum the position integration in
 *  `update()` already uses) so the vertex shader can derive a direction and a
 *  length purely from data already being computed anyway; nothing new is
 *  stored per particle. Effect: a fresh ember still carrying a sprinting
 *  bone's inherited velocity (see `INHERIT_RELAX_S`) streaks, then shortens
 *  back to a round drifting spark as that velocity relaxes to zero — the
 *  trail length is a direct, free readout of the same physics already
 *  driving the position. */
const TRAIL_STRETCH_PER_SPEED = 1.3;
/** Cap on how long the tail can stretch (multiples of aSize) — keeps a full
 *  sprint from drawing a spear instead of a spark. */
const TRAIL_MAX_STRETCH = 11.0;
/** Owner, 2026-08-13: "like a line thing" — a stretched-but-still-round quad
 *  reads as an oval, not a line, because its width stays `aSize` the whole
 *  way out. This narrows the width toward `TRAIL_MIN_WIDTH_SCALE` as stretch
 *  approaches `TRAIL_MAX_STRETCH`, so a fast-moving ember draws a thin streak
 *  and a slow/still one stays the original round spark (width scale 1 at
 *  stretch 1). */
const TRAIL_MIN_WIDTH_SCALE = 0.3;

const VERTEX_SHADER = /* glsl */ `
attribute float aLife;   // 0 at birth, 1 at death
attribute float aSize;   // world-space quad size, metres
attribute vec3 aColor;
attribute vec3 aVelocity; // world-space, metres/second
varying float vLife;
varying vec3 vColor;
varying vec2 vUv;

void main() {
  vLife = aLife;
  vColor = aColor;
  vUv = uv;
  // Camera-facing billboard: take the instance's WORLD POSITION only (drop its
  // rotation/scale) so every quad faces the camera regardless of the player's
  // yaw or the bone's own orientation — an ember does not inherit a wrist's tilt.
  vec4 instanceWorld = instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
  vec4 mvPosition = modelViewMatrix * instanceWorld;

  // Rotate the quad's local axes to align with the ember's on-screen travel
  // direction, then stretch only the trailing half (position.y < 0, which the
  // plane's default UV maps to vUv.y = 0) so the leading edge stays a compact
  // spark tip and the tail draws out behind it — a comet shape, not a bar
  // symmetric about the particle's true position.
  vec3 viewVel = mat3(modelViewMatrix) * aVelocity;
  float speed = length(viewVel.xy);
  vec2 dir = speed > 0.0001 ? viewVel.xy / speed : vec2(0.0, 1.0);
  vec2 axisX = vec2(dir.y, -dir.x);
  vec2 axisY = dir;
  float stretch = clamp(speed * ${TRAIL_STRETCH_PER_SPEED.toFixed(2)}, 1.0, ${TRAIL_MAX_STRETCH.toFixed(1)});
  float lenScale = position.y > 0.0 ? 1.0 : stretch;
  // Width narrows as the trail lengthens — a long thin streak, not a stretched oval.
  float widthScale = mix(1.0, ${TRAIL_MIN_WIDTH_SCALE.toFixed(2)}, (stretch - 1.0) / ${(TRAIL_MAX_STRETCH - 1).toFixed(1)});

  mvPosition.xy += axisX * position.x * aSize * widthScale + axisY * position.y * aSize * lenScale;
  gl_Position = projectionMatrix * mvPosition;
}
`;

const FRAGMENT_SHADER = /* glsl */ `
varying float vLife;
varying vec3 vColor;
varying vec2 vUv;

void main() {
  // Soft round falloff — no texture, a smoothstep on distance from the quad
  // centre, same "procedural, no sampled texture" discipline the retired shell
  // used for its flame noise. On a trail-stretched quad this UV-space circle
  // maps to an elongated, soft-capped streak in world space for free.
  float d = length(vUv - 0.5) * 2.0;
  float disc = 1.0 - smoothstep(0.55, 1.0, d);
  if (disc <= 0.0) discard;

  // Dimmer at the trailing tip (vUv.y -> 0) than the leading edge (vUv.y -> 1)
  // — a comet fade, not a uniformly bright bar.
  float trailFade = mix(0.45, 1.0, vUv.y);

  // Fade in fast (first ~12% of life, so a same-frame spawn never pops fully
  // bright), hold, fade out over the last third.
  float fadeIn = smoothstep(0.0, 0.12, vLife);
  float fadeOut = 1.0 - smoothstep(0.62, 1.0, vLife);
  float a = disc * trailFade * fadeIn * fadeOut;
  if (a <= 0.002) discard;

  // HDR-bright core: crosses the postfx bloom pass's luminance threshold so
  // the existing bloom halo picks it up instead of a shader-drawn fake one.
  gl_FragColor = vec4(vColor * ${GLOW_INTENSITY.toFixed(2)}, a);
}
`;

interface Particle {
  x: number; y: number; z: number;
  /** Inherited velocity — what the emission bone was doing at spawn, relaxed
   *  toward zero over `INHERIT_RELAX_S` (see the integration step). This is
   *  what keeps an ember from being left instantly behind a sprinting player;
   *  without it a particle at rise speed ~0.6 m/s cannot keep pace with a body
   *  moving 5-8 m/s and reads as static debris he ran away from, not as
   *  something that left him. */
  ivx: number; ivy: number; ivz: number;
  /** Own drift — small, constant, added on top of the (decaying) inherited
   *  velocity every frame. */
  vx: number; vy: number; vz: number;
  age: number; life: number;
  /** Boost tier at spawn, baked in so a mid-flight tier change (e.g. tier 2
   *  expiring back to tier 1) recolours only NEW embers, not ones already
   *  in the air — the same "bake it in at spawn" rule `life`/rise speed
   *  already follow. */
  tier: number;
  /** Baked at spawn: SIZE_JITTER's per-particle multiplier and FLICKER's
   *  phase offset. See both constants' doc comments — these exist to break
   *  the "every ember at the same age looks identical" read, not to change
   *  how many embers there are or how big they are on average. */
  sizeScale: number;
  flickerPhase: number;
}

/** Multiplies the age-based size curve per particle, e.g. 0.82-1.18 at
 *  SIZE_JITTER=0.18. Without this every ember at a given lifetime fraction is
 *  the EXACT same size as every other — a particle system's most common
 *  "stamped out of a mold" tell. Symmetric around 1.0, so average silhouette
 *  density (and the spawn-rate tuning above) is unaffected; this only spreads
 *  the sizes already being drawn, it does not draw more or bigger ones. */
const SIZE_JITTER = 0.18;
/** A gentle sine on each ember's own brightness, keyed off a random phase
 *  baked at spawn, folded into the colour written to `aColor` (no shader
 *  change, no new attribute — the same field the tier lerp already writes).
 *  Real embers do not fade along one smooth curve, they pulse as they cool;
 *  this is that pulse, not a strobe — FLICKER_HZ keeps it below the rate a
 *  1-second-lived particle could complete more than a couple of cycles of. */
const FLICKER_AMOUNT = 0.22;
const FLICKER_HZ = 5.5;

/** Seconds for the inherited spawn velocity to relax to zero. Short: this is
 *  what turns "flying off with him forever" into "left his hand and is now
 *  floating free", which is the read an ember needs. */
const INHERIT_RELAX_S = 0.22;

/** Lerp two `Color`s into `out` by `t` in [0,1]. Allocation-free per call. */
function lerpColorInto(a: Color, b: Color, t: number, out: Color): Color {
  out.r = a.r + (b.r - a.r) * t;
  out.g = a.g + (b.g - a.g) * t;
  out.b = a.b + (b.b - a.b) * t;
  return out;
}

export function createBoostAuraSystem(): RenderSubsystem {
  let ctxRef: RenderContext | null = null;
  let mesh: InstancedMesh | null = null;
  let material: ShaderMaterial | null = null;
  let lifeAttr: InstancedBufferAttribute | null = null;
  let sizeAttr: InstancedBufferAttribute | null = null;
  let colorAttr: InstancedBufferAttribute | null = null;
  let velocityAttr: InstancedBufferAttribute | null = null;

  const particles: Particle[] = [];
  let spawnCarry = 0;

  /** Cached ember rig, rebuilt when the silhouette's identity changes — the
   *  same swap-safety pattern the retired hull used, kept because the failure
   *  mode (stale bone from a tier no longer drawn) is identical. */
  let rigKey: readonly object[] | null = null;
  let rig: EmberRig | null = null;

  /** One world position and one velocity per `rig.points` entry, parallel
   *  arrays. Refreshed once per frame in `updatePointVelocities` — velocity is
   *  a FRAME rate, not a per-spawn cost, however many embers spawn off it. */
  let pointPos: Vector3[] = [];
  let pointVel: Vector3[] = [];
  let pointPosValid = false;

  let tier = 0;
  const core = CORE_COLOR.map((c) => new Color(c));
  const edge = EDGE_COLOR.map((c) => new Color(c));
  const scratchColor = new Color();
  const scratchM = new Matrix4();
  const scratchV = new Vector3();

  function ensureRig(slot: number): EmberRig | null {
    if (ctxRef === null) return null;
    const src = ctxRef.attachments.silhouette(slot);
    if (src === null || src.length === 0) {
      rigKey = null; rig = null;
      return null;
    }
    if (rigKey === src) return rig;
    const bones = src[0]?.skeleton?.bones ?? [];
    const hands = findHandRig(bones);
    rig = bones.length > 0 ? findEmberRig(bones, hands) : null;
    rigKey = src;
    // A rig swap invalidates last frame's world positions — a velocity taken
    // between a bone on the OLD skeleton and one on the NEW would be a spike
    // to wherever the previous tier happened to be, not real motion.
    pointPos = rig ? rig.points.map(() => new Vector3()) : [];
    pointVel = rig ? rig.points.map(() => new Vector3()) : [];
    pointPosValid = false;
    return rig;
  }

  /** Once per frame: read each emission bone's world position and derive its
   *  velocity from the delta since last frame. This is what lets a spawned
   *  ember start out moving WITH a sprinting player instead of being dropped
   *  where his hip was a frame ago. */
  function updatePointVelocities(dt: number): void {
    if (rig === null || dt <= 0) return;
    const invDt = 1 / dt;
    for (let i = 0; i < rig.points.length; i++) {
      const bone = rig.points[i]!;
      bone.updateWorldMatrix(true, false);
      const prev = pointPos[i]!;
      const vel = pointVel[i]!;
      const e = bone.matrixWorld.elements;
      if (pointPosValid) {
        vel.set((e[12]! - prev.x) * invDt, (e[13]! - prev.y) * invDt, (e[14]! - prev.z) * invDt);
      } else {
        vel.set(0, 0, 0);
      }
      prev.set(e[12]!, e[13]!, e[14]!);
    }
    pointPosValid = true;
  }

  function spawn(count: number): void {
    if (rig === null || rig.points.length === 0) return;
    for (let n = 0; n < count && particles.length < MAX_PARTICLES; n++) {
      const idx = (Math.random() * rig.points.length) | 0;
      const bone = rig.points[idx]!;
      const inherited = pointVel[idx];
      bone.updateWorldMatrix(true, false);
      scratchV.setFromMatrixPosition(bone.matrixWorld);
      const life = LIFE_S[tier] ?? 0.9;
      const angle = Math.random() * Math.PI * 2;
      particles.push({
        x: scratchV.x + (Math.random() * 2 - 1) * SPAWN_JITTER_M,
        y: scratchV.y + (Math.random() * 2 - 1) * SPAWN_JITTER_M * 0.6,
        z: scratchV.z + (Math.random() * 2 - 1) * SPAWN_JITTER_M,
        ivx: inherited?.x ?? 0, ivy: inherited?.y ?? 0, ivz: inherited?.z ?? 0,
        vx: Math.cos(angle) * DRIFT_SPEED_M_S,
        vy: (RISE_SPEED[tier] ?? 0.6) * (0.75 + Math.random() * 0.5),
        vz: Math.sin(angle) * DRIFT_SPEED_M_S,
        age: 0,
        life,
        tier,
        sizeScale: 1 - SIZE_JITTER + Math.random() * SIZE_JITTER * 2,
        flickerPhase: Math.random() * Math.PI * 2,
      });
    }
  }

  return {
    name: 'boostAura',

    init(ctx: RenderContext): void {
      ctxRef = ctx;
      const geo = new PlaneGeometry(1, 1);

      const life = new Float32Array(MAX_PARTICLES);
      const size = new Float32Array(MAX_PARTICLES);
      const color = new Float32Array(MAX_PARTICLES * 3);
      const velocity = new Float32Array(MAX_PARTICLES * 3);
      lifeAttr = new InstancedBufferAttribute(life, 1);
      sizeAttr = new InstancedBufferAttribute(size, 1);
      colorAttr = new InstancedBufferAttribute(color, 3);
      velocityAttr = new InstancedBufferAttribute(velocity, 3);
      lifeAttr.setUsage(DynamicDrawUsage);
      sizeAttr.setUsage(DynamicDrawUsage);
      colorAttr.setUsage(DynamicDrawUsage);
      velocityAttr.setUsage(DynamicDrawUsage);
      geo.setAttribute('aLife', lifeAttr);
      geo.setAttribute('aSize', sizeAttr);
      geo.setAttribute('aColor', colorAttr);
      geo.setAttribute('aVelocity', velocityAttr);

      material = new ShaderMaterial({
        vertexShader: VERTEX_SHADER,
        fragmentShader: FRAGMENT_SHADER,
        transparent: true,
        depthWrite: false,
        // ⚠ DEPTH TEST OFF, AND THIS IS NOT OPTIONAL. Every emission bone (hip,
        // wrists, ankles) sits INSIDE the character's own mesh volume, not on
        // its surface — a hip joint is well inside the pelvis, an ankle is
        // inside the boot. With depth testing on, the already-drawn opaque body
        // in front of that world position wins on almost every frame, so a
        // correctly-positioned, correctly-coloured particle renders and is
        // then invisible behind his own skin. Proved, not assumed: a driven
        // capture showed `embers.count` climbing to the expected steady-state
        // (4, matching spawnRate x life) while ZERO pixels were visible in the
        // frame at full resolution. Turning depthTest off is what every
        // bone-anchored character VFX in practice does for exactly this
        // reason — a handful of small, sparse, transparent quads rendering
        // "on top" reads as embers leaving him, not as a depth bug.
        depthTest: false,
        // ⚠ TRIED ADDITIVE, REVERTED, AND THE ACES NOTE BELOW IS WHY. The
        // reasoning at the time was "a handful of small particles cover too
        // little of the frame for the shell's amber-over-grass problem to
        // apply" — that reasoning was about HUE, and it was right: additive
        // embers did not turn amber. What it missed is BRIGHTNESS. An additive
        // blend at meaningful alpha adds close to the full core colour's light
        // to whatever is behind it, and this game's ACES tone-mapping
        // compresses bright, saturated colour toward pale/white at high
        // luminance — so at the alpha needed to be visible at all, every
        // ember rendered as a washed-out cream smear instead of red. Standard
        // alpha blending REPLACES the background in proportion to alpha, so
        // the colour on screen is the colour authored, the same fix the shell
        // made for the same reason. Confirmed by rendering both.
        blending: NormalBlending,
      });

      const im = new InstancedMesh(geo, material, MAX_PARTICLES);
      im.name = 'boost-aura-embers';
      im.frustumCulled = false;
      im.instanceMatrix.setUsage(DynamicDrawUsage);
      im.count = 0;
      mesh = im;
      ctx.scene.add(im);
    },

    update(state: RenderState): void {
      const im = mesh;
      const lAttr = lifeAttr, sAttr = sizeAttr, cAttr = colorAttr, vAttr = velocityAttr;
      if (im === null || lAttr === null || sAttr === null || cAttr === null || vAttr === null || ctxRef === null) return;

      const want = state.boostAuraTier > 0 ? Math.min(2, state.boostAuraTier) : 0;
      if (want > 0) tier = want;
      const dt = Math.max(state.dt, 0);

      if (want > 0) {
        const slot = state.boostAuraSlot;
        if (slot >= 0) {
          ensureRig(slot);
          // Velocity BEFORE spawn: a particle born this frame must inherit
          // this frame's motion, not last frame's — otherwise the very first
          // ember of a fresh sprint starts from zero and is instantly behind.
          updatePointVelocities(dt);
          spawnCarry += (SPAWN_RATE[tier] ?? 0) * dt;
          const n = spawnCarry | 0;
          if (n > 0) {
            spawn(n);
            spawnCarry -= n;
          }
        }
      } else {
        spawnCarry = 0;
        pointPosValid = false;
      }

      if (particles.length === 0) {
        if (im.count !== 0) im.count = 0;
        return;
      }

      // Age, integrate, drop the dead — in place, so the array never regrows
      // past its high-water mark for this boost.
      //
      // The inherited component relaxes toward zero (exponential, frame-rate
      // independent — the same idiom `handAttach.ts`'s held-offset tracker
      // uses) while the particle's own rise/drift is added UNSCALED every
      // frame. Early in life the ember all but rides along with the bone that
      // spawned it — visually "attached" — and within INHERIT_RELAX_S it has
      // let go and is doing nothing but its own small rise and wander, which
      // is the "left him and is now floating" read a spark needs.
      const relax = 1 - Math.exp(-dt / INHERIT_RELAX_S);
      let write = 0;
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i]!;
        p.age += dt;
        if (p.age >= p.life) continue;
        p.x += (p.ivx + p.vx) * dt;
        p.y += (p.ivy + p.vy) * dt;
        p.z += (p.ivz + p.vz) * dt;
        p.ivx -= p.ivx * relax;
        p.ivy -= p.ivy * relax;
        p.ivz -= p.ivz * relax;
        particles[write++] = p;
      }
      particles.length = write;

      const count = Math.min(particles.length, MAX_PARTICLES);
      im.count = count;
      for (let i = 0; i < count; i++) {
        const p = particles[i]!;
        const t = p.age / p.life;
        scratchM.makeTranslation(p.x, p.y, p.z);
        im.setMatrixAt(i, scratchM);

        lAttr.setX(i, t);
        // Size ramps SIZE_START -> SIZE_PEAK over the first third, then
        // SIZE_PEAK -> SIZE_END over the rest — a spark that blooms then
        // shrinks as it burns out, rather than a quad that pops to full size.
        // p.sizeScale (baked at spawn) spreads that curve across particles so
        // they are not all identical at the same age.
        const size = (t < 0.33
          ? SIZE_START + (SIZE_PEAK - SIZE_START) * (t / 0.33)
          : SIZE_PEAK + (SIZE_END - SIZE_PEAK) * ((t - 0.33) / 0.67)) * p.sizeScale;
        sAttr.setX(i, size);

        lerpColorInto(core[p.tier] ?? core[1]!, edge[p.tier] ?? edge[1]!, Math.min(1, t * 1.4), scratchColor);
        // Gentle per-particle flicker on top of the age-based hue lerp — see
        // FLICKER_AMOUNT's doc comment. Folded into colour, not alpha, so a
        // bright beat reads as "this spark just glowed hotter" (and can even
        // nudge it over the bloom threshold for an instant) rather than as
        // the particle flashing in and out of visibility.
        const flicker = 1 + FLICKER_AMOUNT * Math.sin(p.age * FLICKER_HZ * Math.PI * 2 + p.flickerPhase);
        cAttr.setXYZ(i, scratchColor.r * flicker, scratchColor.g * flicker, scratchColor.b * flicker);

        // Post-integration velocity (inherited + own, this frame's decayed
        // value) — the vertex shader stretches the quad along this, so the
        // trail is a direct readout of the same motion that just moved p.
        vAttr.setXYZ(i, p.ivx + p.vx, p.ivy + p.vy, p.ivz + p.vz);
      }
      lAttr.needsUpdate = true;
      sAttr.needsUpdate = true;
      cAttr.needsUpdate = true;
      vAttr.needsUpdate = true;
      im.instanceMatrix.needsUpdate = true;
    },

    dispose(): void {
      particles.length = 0;
      if (mesh) ctxRef?.scene.remove(mesh);
      mesh?.geometry.dispose();
      material?.dispose();
      mesh = null;
      material = null;
      lifeAttr = null;
      sizeAttr = null;
      colorAttr = null;
      velocityAttr = null;
      ctxRef = null;
    },
  };
}
